import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

export type TaskListFilters = {
	/** null while the route has no usable area, which is what keeps the query from running. */
	assetId: number | null;
	craftIds: string[];
	typeCode: string;
	searchText: string;
	downHours: string;
};

const parseCrafts = (raw: string | null): string[] => {
	if (!raw || raw === "A") return ["A"];
	const valid = raw
		.split(",")
		.filter((craft) => craft === "A" || Number.parseInt(craft, 10) >= 1);
	return valid.length ? valid : ["A"];
};

/**
 * The filters, read straight from the URL and the route on every render.
 *
 * This is deliberately synchronous and has no state of its own: the asset comes from the route
 * parameter, so it is known on the first render instead of arriving later from the asset list, and
 * the defaults are applied here rather than by writing them into the URL first. `useInitialValues`
 * still puts them in the address bar, but since it writes the same values this hook already
 * returned, the query key does not change and no second request goes out.
 */
export const useTaskListFilters = (): TaskListFilters => {
	const [searchParams] = useSearchParams();
	const { AssetIdParam } = useParams();

	const craft = searchParams.get("craft");
	const type = searchParams.get("type");
	const search = searchParams.get("search");
	const downHours = searchParams.get("downHours");

	return useMemo(() => {
		const assetId = Number(AssetIdParam);

		return {
			assetId: Number.isInteger(assetId) ? assetId : null,
			craftIds: parseCrafts(craft),
			typeCode: type === "PM" || type === "RM" ? type : "A",
			searchText: search ?? "",
			downHours: downHours ?? "",
		};
	}, [AssetIdParam, craft, type, search, downHours]);
};
